import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  getAllCityConfigs, 
  createCityConfigDocument, 
  updateCityConfigDocument, 
  deleteCityConfigDocument,
  initializePredefinedCities,
  updateCityStats,
  searchCities,
  getCitiesWithAgodaIntegration
} from '../../utils/cityConfigUtils';
import { 
  cityConfigModel, 
  formatCityConfigForDisplay,
  predefinedThaiCities 
} from '../../data/cityConfigModel';

const CityConfigManager = () => {
  const { currentUser } = useAuth();
  const [cityConfigs, setCityConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deletingCityId, setDeletingCityId] = useState(null);
  const [initializing, setInitializing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    cityName: '',
    cityNameEn: '',
    countryCode: 'TH',
    countryName: 'Thailand',
    latitude: 0,
    longitude: 0,
    timezone: 'Asia/Bangkok',
    agodaSettings: {
      cityId: '',
      isActive: true,
      searchRadius: 50,
      defaultLanguage: 'th',
      currency: 'THB',
      maxResults: 20
    },
    propertyTypes: {
      hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'boutique', 'luxury'] },
      restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe', 'fine_dining', 'street_food'] },
      attractions: { enabled: false, priority: 3, categories: ['attraction', 'museum', 'park', 'temple'] }
    },
    status: 'active',
    isDefault: false
  });

  // Fetch city configurations
  useEffect(() => {
    fetchCityConfigs();
  }, []);

  const fetchCityConfigs = async () => {
    setLoading(true);
    try {
      const result = await getAllCityConfigs();
      if (result.success) {
        setCityConfigs(result.data);
      } else {
        console.error('Error fetching city configs:', result.error);
      }
    } catch (error) {
      console.error('Error fetching city configs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize predefined cities
  const handleInitializeCities = async () => {
    if (!currentUser?.uid) return;
    
    setInitializing(true);
    try {
      const result = await initializePredefinedCities(currentUser.uid);
      if (result.success) {
        alert(`Successfully initialized ${result.created} city configurations`);
        fetchCityConfigs();
      } else {
        alert(`Error initializing cities: ${result.error}`);
      }
    } catch (error) {
      console.error('Error initializing cities:', error);
      alert('Error initializing cities');
    } finally {
      setInitializing(false);
    }
  };

  // Create new city configuration
  const handleCreateCity = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) return;

    try {
      const result = await createCityConfigDocument(formData, currentUser.uid);
      if (result.success) {
        alert('City configuration created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchCityConfigs();
      } else {
        alert(`Error creating city: ${result.error}`);
      }
    } catch (error) {
      console.error('Error creating city:', error);
      alert('Error creating city');
    }
  };

  // Update city configuration
  const handleUpdateCity = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid || !selectedCity) return;

    try {
      const result = await updateCityConfigDocument(selectedCity.id, formData, currentUser.uid);
      if (result.success) {
        alert('City configuration updated successfully');
        setShowEditModal(false);
        setSelectedCity(null);
        resetForm();
        fetchCityConfigs();
      } else {
        alert(`Error updating city: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating city:', error);
      alert('Error updating city');
    }
  };

  // Delete city configuration
  const handleDeleteCity = async (cityId) => {
    if (!window.confirm('Are you sure you want to delete this city configuration?')) return;

    setDeletingCityId(cityId);
    try {
      const result = await deleteCityConfigDocument(cityId);
      if (result.success) {
        alert('City configuration deleted successfully');
        fetchCityConfigs();
      } else {
        alert(`Error deleting city: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting city:', error);
      alert('Error deleting city');
    } finally {
      setDeletingCityId(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      cityName: '',
      cityNameEn: '',
      countryCode: 'TH',
      countryName: 'Thailand',
      latitude: 0,
      longitude: 0,
      timezone: 'Asia/Bangkok',
      agodaSettings: {
        cityId: '',
        isActive: true,
        searchRadius: 50,
        defaultLanguage: 'th',
        currency: 'THB',
        maxResults: 20
      },
      propertyTypes: {
        hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'boutique', 'luxury'] },
        restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe', 'fine_dining', 'street_food'] },
        attractions: { enabled: false, priority: 3, categories: ['attraction', 'museum', 'park', 'temple'] }
      },
      status: 'active',
      isDefault: false
    });
  };

  // Open edit modal
  const openEditModal = (city) => {
    setSelectedCity(city);
    setFormData({
      ...city,
      agodaSettings: city.agodaSettings || {
        cityId: '',
        isActive: true,
        searchRadius: 50,
        defaultLanguage: 'th',
        currency: 'THB',
        maxResults: 20
      },
      propertyTypes: city.propertyTypes || {
        hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'boutique', 'luxury'] },
        restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe', 'fine_dining', 'street_food'] },
        attractions: { enabled: false, priority: 3, categories: ['attraction', 'museum', 'park', 'temple'] }
      }
    });
    setShowEditModal(true);
  };

  // Filter cities
  const filteredCities = cityConfigs.filter(city => {
    const matchesSearch = city.cityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         city.cityNameEn.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || city.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">City Configuration Manager</h1>
        <p className="text-gray-600">Manage city configurations for Agoda integration</p>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <span>+</span>
          Add New City
        </button>
        
        <button
          onClick={handleInitializeCities}
          disabled={initializing}
          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          {initializing ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <span>🚀</span>
          )}
          Initialize Predefined Cities
        </button>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search cities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          {cityConfigModel.statusOptions.map(status => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      {/* Cities Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                City
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Country
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Coordinates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agoda Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCities.map((city) => {
              const formattedCity = formatCityConfigForDisplay(city);
              return (
                <tr key={city.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{city.cityName}</div>
                      <div className="text-sm text-gray-500">{city.cityNameEn}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {city.countryName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formattedCity.formattedCoordinates}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      city.agodaSettings?.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {city.agodaSettings?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${formattedCity.statusColor}`}>
                      {formattedCity.statusDisplay}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(city)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCity(city.id)}
                        disabled={deletingCityId === city.id}
                        className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                      >
                        {deletingCityId === city.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredCities.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No cities found matching your criteria
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CityConfigModal
          title="Create New City Configuration"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreateCity}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCity && (
        <CityConfigModal
          title="Edit City Configuration"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdateCity}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCity(null);
            resetForm();
          }}
        />
      )}
    </div>
  );
};

// City Configuration Modal Component
const CityConfigModal = ({ title, formData, setFormData, onSubmit, onClose }) => {
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePropertyTypeChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      propertyTypes: {
        ...prev.propertyTypes,
        [type]: {
          ...prev.propertyTypes[type],
          [field]: value
        }
      }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City Name (Thai)
              </label>
              <input
                type="text"
                value={formData.cityName}
                onChange={(e) => handleInputChange('cityName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City Name (English)
              </label>
              <input
                type="text"
                value={formData.cityNameEn}
                onChange={(e) => handleInputChange('cityNameEn', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country Code
              </label>
              <input
                type="text"
                value={formData.countryCode}
                onChange={(e) => handleInputChange('countryCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country Name
              </label>
              <input
                type="text"
                value={formData.countryName}
                onChange={(e) => handleInputChange('countryName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="0.0001"
                value={formData.latitude}
                onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="0.0001"
                value={formData.longitude}
                onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Agoda Settings */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Agoda Integration Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agoda City ID
                </label>
                <input
                  type="text"
                  value={formData.agodaSettings.cityId}
                  onChange={(e) => handleInputChange('agodaSettings.cityId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Radius (km)
                </label>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={formData.agodaSettings.searchRadius}
                  onChange={(e) => handleInputChange('agodaSettings.searchRadius', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Results
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.agodaSettings.maxResults}
                  onChange={(e) => handleInputChange('agodaSettings.maxResults', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="agodaActive"
                  checked={formData.agodaSettings.isActive}
                  onChange={(e) => handleInputChange('agodaSettings.isActive', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="agodaActive" className="ml-2 block text-sm text-gray-900">
                  Enable Agoda Integration
                </label>
              </div>
            </div>
          </div>

          {/* Property Types */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Types</h3>
            <div className="space-y-4">
              {cityConfigModel.propertyTypeOptions.map(type => (
                <div key={type.value} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{type.icon}</span>
                      <span className="font-medium">{type.label}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.propertyTypes[type.value]?.enabled || false}
                      onChange={(e) => handlePropertyTypeChange(type.value, 'enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.propertyTypes[type.value]?.priority || 1}
                        onChange={(e) => handlePropertyTypeChange(type.value, 'priority', parseInt(e.target.value))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="border-t pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {cityConfigModel.statusOptions.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
                  Set as Default City
                </label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              Save Configuration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CityConfigManager;
